import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mail, Paperclip, FileText, FileCheck, Receipt as ReceiptIcon, AlertCircle, ChevronDown, ChevronUp, Inbox } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { EmailRecipients } from '../../components/admin/EmailRecipients';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  quote:      { label: 'Quote',           icon: FileCheck,   color: 'bg-blue-100 text-blue-800 border-blue-200' },
  invoice:    { label: 'Invoice',         icon: FileText,    color: 'bg-purple-100 text-purple-800 border-purple-200' },
  receipt:    { label: 'Receipt',         icon: ReceiptIcon, color: 'bg-green-100 text-green-800 border-green-200' },
  reminder:   { label: 'Reminder',        icon: AlertCircle, color: 'bg-amber-100 text-amber-800 border-amber-200' },
  travelsafe: { label: 'Readiness',       icon: FileText,    color: 'bg-teal-100 text-teal-800 border-teal-200' },
  general:    { label: 'General',         icon: Mail,        color: 'bg-gray-100 text-gray-800 border-gray-200' },
};

const CATEGORY_ROUTE = {
  quote: 'quotes',
  invoice: 'invoices',
  receipt: 'receipts',
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return formatDate(iso);
  }
};

export const ClientEmailHistory = () => {
  const { clientEmail } = useParams();
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');

  const decodedEmail = decodeURIComponent(clientEmail || '');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientEmail]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/emails?email=${encodeURIComponent(decodedEmail)}`,
        { headers: { 'Authorization': `Basic ${btoa(':' + password)}` } },
      );
      if (response.ok) {
        setEmails(await response.json());
      } else if (response.status === 401) {
        navigate('/admin/login');
      }
    } catch (err) {
      logger.error('Failed to fetch email history:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = filter === 'all' ? emails : emails.filter((e) => e.category === filter);

  const counts = emails.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + 1;
    return acc;
  }, {});

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const openRelated = (email) => {
    const route = CATEGORY_ROUTE[email.related_type];
    if (route && email.related_id) {
      navigate(`/admin/${route}/${email.related_id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Email History
              </h1>
              <p className="text-sm text-foreground/60" data-testid="client-email-header">
                All emails sent to <span className="font-mono font-medium text-foreground">{decodedEmail}</span>
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/clients')}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 transition-colors"
            >
              ← Back to Clients
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-foreground/60">Loading email history…</p>
          </div>
        ) : emails.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-12 text-center">
            <Inbox className="w-16 h-16 text-foreground/30 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-1">No emails yet</h2>
            <p className="text-sm text-foreground/60">
              Emails sent from quotes, invoices, receipts, reminders, or readiness reports will appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Filter chips */}
            <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="email-filter-chips">
              <button
                onClick={() => setFilter('all')}
                data-testid="filter-all"
                className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                  filter === 'all'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-foreground border-border hover:bg-gray-50'
                }`}
              >
                All ({emails.length})
              </button>
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const count = counts[key] || 0;
                if (count === 0) return null;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    data-testid={`filter-${key}`}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                      filter === key
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-foreground border-border hover:bg-gray-50'
                    }`}
                  >
                    {meta.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Email list */}
            <div className="space-y-3" data-testid="email-list">
              {filtered.map((email) => {
                const meta = CATEGORY_META[email.category] || CATEGORY_META.general;
                const Icon = meta.icon;
                const isOpen = expanded[email.id];
                const isFailed = email.status === 'failed';

                return (
                  <div
                    key={email.id}
                    data-testid={`email-card-${email.id}`}
                    className={`bg-white border rounded-xl overflow-hidden transition-all ${
                      isFailed ? 'border-red-200' : 'border-border'
                    }`}
                  >
                    {/* Card header */}
                    <button
                      data-testid={`client-email-expand-${email.id}`}
                      onClick={() => toggleExpand(email.id)}
                      className="w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color.replace('text-', 'bg-').replace('bg-bg-', 'bg-').split(' ')[0]}/30`}>
                            <Icon className={`w-5 h-5 ${meta.color.split(' ')[1]}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${meta.color}`}>
                                {meta.label}
                              </span>
                              {email.related_number && (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => { e.stopPropagation(); openRelated(email); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openRelated(email); } }}
                                  className="font-mono text-xs text-primary hover:underline cursor-pointer"
                                  data-testid={`email-related-${email.id}`}
                                >
                                  {email.related_number}
                                </span>
                              )}
                              {isFailed && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                                  Failed
                                </span>
                              )}
                              {email.attachments?.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-foreground/60">
                                  <Paperclip className="w-3 h-3" />
                                  {email.attachments.length}
                                </span>
                              )}
                            </div>
                            <p className="font-semibold text-foreground truncate">{email.subject}</p>
                            <p className="text-xs text-foreground/60 mt-0.5">
                              {formatTimestamp(email.sent_at)} · to {email.to_email}
                            </p>
                            <EmailRecipients email={email} testId={`client-email-recipients-${email.id}`} />
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-foreground/40">
                          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </button>

                    {/* Expanded body */}
                    {isOpen && (
                      <div className="border-t border-border bg-gray-50/50 p-5 space-y-4">
                        {email.attachments?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide mb-2">Attachments</p>
                            <div className="flex flex-wrap gap-2">
                              {email.attachments.map((name, idx) => (
                                <span
                                  key={`${email.id}-att-${idx}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-border rounded-full text-xs font-medium text-foreground/80"
                                >
                                  <Paperclip className="w-3 h-3" />
                                  {name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {isFailed && email.error && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-1">Send failed</p>
                            <p className="text-sm text-red-700 font-mono">{email.error}</p>
                          </div>
                        )}

                        <div>
                          <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide mb-2">Message body</p>
                          <div
                            className="bg-white border border-border rounded-lg p-4 max-h-96 overflow-y-auto text-sm prose prose-sm max-w-none"
                            data-testid={`email-body-${email.id}`}
                            dangerouslySetInnerHTML={{ __html: email.body_html || '' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
